import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    AlternativePaymentMethodIntegrationParameters,
    AlternativePaymentMethodIntegrationUpdateParameters,
    getRepositoryQueryKey,
    useRepositoryQuery,
    useRepositoryRegistry,
} from '@mews-commander/data-access';
import { RouteName } from '@mews-commander/enums';
import { useResolveRoute } from '@mews/routing';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { deleteEnterpriseIntegration } from '@mews-commander/legacy-entities/src/ducks/enterpriseIntegrations';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { navigateInModal } from '../../app/actions/common';

export const useGetAlternativePaymentMethodIntegration = ({ id }: AlternativePaymentMethodIntegrationParameters) =>
    useRepositoryQuery('alternativePaymentMethodIntegration', 'get', { id });

export const useUpdateAlternativePaymentMethodIntegration = () => {
    const { alternativePaymentMethodIntegration } = useRepositoryRegistry();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, paymentMethods }: AlternativePaymentMethodIntegrationUpdateParameters) => {
            await alternativePaymentMethodIntegration.update({ id, paymentMethods });

            void queryClient.invalidateQueries({
                queryKey: getRepositoryQueryKey('alternativePaymentMethodIntegration', 'get', {
                    id,
                }),
            });
        },
    });
};

export const useDeleteAlternativePaymentMethodIntegration = ({ id }: AlternativePaymentMethodIntegrationParameters) => {
    const { alternativePaymentMethodIntegration } = useRepositoryRegistry();
    const dispatch = useCommanderThunkDispatch();
    const resolveRoute = useResolveRoute();
    const currentEnterpriseId = useCurrentEnterpriseId();

    return useMutation({
        mutationFn: async () => alternativePaymentMethodIntegration.delete({ id }),
        onSuccess: () => {
            if (currentEnterpriseId) {
                dispatch(deleteEnterpriseIntegration({ integrationId: id, enterpriseId: currentEnterpriseId }));

                const url = resolveRoute(RouteName.EnterpriseIntegrationMarketplace, {
                    query: {
                        OwnerId: currentEnterpriseId,
                        CategoryId: 'subscriptions',
                    },
                });

                dispatch(navigateInModal(url));
            } else {
                dispatch(navigateInModal(resolveRoute(RouteName.EnterpriseIntegrationReportIndex)));
            }
        },
    });
};
